import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCheck, FiClock, FiMapPin, FiShield, FiStar, FiUsers, FiArrowRight } from 'react-icons/fi';
import SEOPageLayout from '../components/SEOPageLayout';
import FadeInView from '../components/FadeInView';
import { destinations, SITE_URL, AIRPORT_COORDS } from '../config/seo-data';

const buildBookingUrl = (pickup, dropoff) => {
  const params = new URLSearchParams();
  if (pickup) {
    params.set('pickup_name', pickup.name);
    params.set('pickup_lat', pickup.lat);
    params.set('pickup_lng', pickup.lng);
  }
  if (dropoff) {
    params.set('dropoff_name', dropoff.name);
    params.set('dropoff_lat', dropoff.lat);
    params.set('dropoff_lng', dropoff.lng);
  }
  return `/booking?${params.toString()}`;
};

const ease = [0.22, 1, 0.36, 1];

const southAreas = ['South Corfu', 'Southeast Corfu'];

const routes = destinations
  .filter((d) => !southAreas.includes(d.area))
  .sort((a, b) => (a.popular === b.popular ? 0 : a.popular ? -1 : 1))
  .map((d) => ({
    ...d,
    link: `/transfers/${d.slug}`,
    bookingLink: d.coords
      ? buildBookingUrl(AIRPORT_COORDS, { name: d.name, ...d.coords })
      : '/booking'
  }));

const features = [
  { icon: FiStar, title: 'Meet & Greet', desc: 'Your driver waits at arrivals with a personalised name sign, no searching, no stress.' },
  { icon: FiClock, title: 'Flight Monitoring', desc: 'We track your flight in real-time. Delayed? We adjust automatically.' },
  { icon: FiShield, title: 'Fixed Prices', desc: 'No meters, no surprises. Your price is confirmed the moment you book.' },
  { icon: FiMapPin, title: 'Door-to-Door', desc: 'Direct to your hotel, villa, or resort entrance. No shared rides.' },
  { icon: FiUsers, title: 'Child Seats', desc: 'Complimentary child & booster seats fitted on request for families.' },
  { icon: FiCheck, title: '24/7 Service', desc: 'Early morning departures, midnight arrivals, we never close.' }
];

const steps = [
  { num: '01', title: 'Book Online', desc: 'Choose destination, date & flight. Instant confirmation.' },
  { num: '02', title: 'We Track Your Flight', desc: 'Real-time monitoring. We adjust if you\'re early or delayed.' },
  { num: '03', title: 'Meet at Arrivals', desc: 'Driver with name sign waiting at the arrivals hall.' },
  { num: '04', title: 'Enjoy the Ride', desc: 'Air-conditioned Mercedes-Benz. Water & WiFi included.' },
  { num: '05', title: 'Door-to-Door', desc: 'Dropped right at your accommodation entrance.' },
];

const stats = [
  { value: '4.9', label: 'Rating', suffix: '/5' },
  { value: '2000', label: 'Transfers', suffix: '+' },
  { value: '24', label: 'Availability', suffix: '/7' },
  { value: '5', label: 'Min to Corfu Town', suffix: 'min' },
];

const AirportTransfers = () => (
  <SEOPageLayout
    title="Lasmari Chauffeurs | Corfu Airport Transfer & CFU Pickup"
    description="Book your Corfu Airport transfer online. Professional meet & greet, flight monitoring, fixed prices, Mercedes-Benz vehicles. Transfers to all Corfu destinations. 24/7 service."
    keywords="corfu airport transfer, CFU airport transfer, corfu airport taxi, corfu airport pickup, corfu airport to hotel transfer, book corfu airport transfer, corfu airport transfer prices, best corfu airport transfer"
    canonical={`${SITE_URL}/airport-transfers`}
  >
    {/* Hero - Full viewport with image background */}
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
      {/* Background image - replace src with your Gemini generated image */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/images/backgrounds/airport-vclass-hero.webp')`,
          }}
        />
        {/* Dark scrim overlay (sits above photo, below text), keeps photo unfiltered */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
        {/* Radial contrast pool behind the centered headline */}
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 65% 55% at 50% 45%, rgba(0,0,0,0.55), transparent 72%)' }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="text-white/85 text-xs uppercase tracking-[0.4em] mb-6 font-light"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}
        >
          Corfu International Airport · CFU
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease, delay: 0.1 }}
          className="text-5xl md:text-6xl lg:text-7xl text-white mb-8 leading-[1.08] tracking-tight"
          style={{ fontFamily: "'Playfair Display', serif", textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 18px rgba(0,0,0,0.6)' }}
        >
          Your Airport
          <br />
          <span className="font-light italic">Transfer Awaits</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.2 }}
          className="text-gray-300 text-lg lg:text-xl max-w-2xl mx-auto mb-10 font-light leading-relaxed"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}
        >
          Professional meet & greet, real-time flight monitoring, and a Mercedes-Benz waiting, every time you land.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to={`/booking?pickup_name=${encodeURIComponent(AIRPORT_COORDS.name)}&pickup_lat=${AIRPORT_COORDS.lat}&pickup_lng=${AIRPORT_COORDS.lng}`}
            className="bg-[#ba1609] text-white px-10 py-4 rounded-full font-medium hover:bg-[#8e1107] transition-all duration-300 text-base tracking-wide"
          >
            Book Airport Transfer
          </Link>
          <a
            href="#how-it-works"
            className="text-white/80 hover:text-white px-8 py-4 rounded-full border border-white/20 hover:border-white/40 transition-all duration-300 text-sm tracking-wide"
          >
            How It Works
          </a>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-[1px] h-12 bg-gradient-to-b from-white/50 to-transparent"
          />
        </motion.div>
      </div>
    </section>

    {/* Stats Bar */}
    <section className="relative py-12 bg-[#faf7f6] border-y border-black/10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <FadeInView
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.1, ease }}
              className="text-center"
            >
              <div className="text-3xl lg:text-4xl text-[#1a1012] mb-1" style={{ fontFamily: "'Prata', serif" }}>
                {stat.value}<span className="text-[#ba1609] text-lg">{stat.suffix}</span>
              </div>
              <p className="text-[#5b4d4f] text-xs uppercase tracking-[0.3em]">{stat.label}</p>
            </FadeInView>
          ))}
        </div>
      </div>
    </section>

    {/* How It Works */}
    <section id="how-it-works" className="py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeInView
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">The Experience</p>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
            How It Works
          </h2>
        </FadeInView>

        <div className="space-y-0">
          {steps.map((step, i) => (
            <FadeInView
              key={step.num}
              initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: i * 0.08, ease }}
              className="flex items-start gap-6 lg:gap-10 py-8 border-b border-black/10 group hover:border-[#ba1609]/40 transition-colors"
            >
              <span className="text-[#ba1609]/30 text-4xl lg:text-6xl font-light shrink-0 w-16 lg:w-24 group-hover:text-[#ba1609]/70 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                {step.num}
              </span>
              <div>
                <h3 className="text-[#1a1012] text-xl lg:text-2xl font-medium mb-2 group-hover:text-[#ba1609] transition-colors">{step.title}</h3>
                <p className="text-[#5b4d4f] text-sm lg:text-base font-light leading-relaxed">{step.desc}</p>
              </div>
            </FadeInView>
          ))}
        </div>
      </div>
    </section>

    {/* Features Grid */}
    <section className="py-24 lg:py-32 bg-[#faf7f6]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeInView
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Included With Every Transfer</p>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
            What's Included
          </h2>
        </FadeInView>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((f, i) => (
            <FadeInView
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.08, ease }}
              className="group relative bg-white border border-black/10 rounded-2xl p-8 shadow-sm hover:border-[#ba1609]/40 hover:shadow-lg hover:shadow-black/5 transition-colors duration-500"
            >
              <div className="w-12 h-12 rounded-xl bg-[#fbeceb] border border-[#ba1609]/20 flex items-center justify-center mb-6 group-hover:bg-[#ba1609]/15 transition-colors duration-500">
                <f.icon className="w-5 h-5 text-[#ba1609]" />
              </div>
              <h3 className="text-[#1a1012] text-lg font-medium mb-3">{f.title}</h3>
              <p className="text-[#5b4d4f] text-sm leading-relaxed font-light">{f.desc}</p>
            </FadeInView>
          ))}
        </div>
      </div>
    </section>

    {/* Destinations */}
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeInView
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 gap-4"
        >
          <div>
            <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Where We Go</p>
            <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Transfer Destinations
            </h2>
          </div>
          <p className="text-[#5b4d4f] text-sm max-w-md font-light">
            Per vehicle, not per person. Every transfer includes meet & greet, flight monitoring, and luggage assistance.
          </p>
        </FadeInView>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {routes.map((r, i) => (
            <FadeInView
              key={r.slug}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.04, ease }}
            >
              <div className="group bg-[#faf7f6] border border-black/10 rounded-xl px-5 py-4 hover:bg-white hover:border-[#ba1609]/40 hover:shadow-sm transition-colors duration-300">
                <div className="flex items-center justify-between">
                  <Link to={r.link} className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-[#1a1012] font-medium group-hover:text-[#ba1609] transition-colors">
                        {r.name}
                      </h3>
                      {r.popular && (
                        <span className="text-[9px] bg-[#fbeceb] text-[#ba1609] px-2 py-0.5 rounded-full uppercase tracking-wider border border-[#ba1609]/20">
                          Popular
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[#5b4d4f] text-xs">
                      <span>{r.distance}</span>
                      <span className="w-1 h-1 rounded-full bg-[#1a1012]/20" />
                      <span>{r.duration}</span>
                      <span className="w-1 h-1 rounded-full bg-[#1a1012]/20" />
                      <span>{r.area}</span>
                    </div>
                  </Link>
                  <Link
                    to={r.bookingLink}
                    className="text-[11px] bg-[#ba1609] hover:bg-[#8e1107] text-white px-3 py-1.5 rounded-full transition-all duration-300 shrink-0 ml-3"
                  >
                    Book
                  </Link>
                </div>
              </div>
            </FadeInView>
          ))}
        </div>
      </div>
    </section>
  </SEOPageLayout>
);

export default AirportTransfers;
